"use strict";
import { onFind } from "@elements/init-modules-in-scope";
import { trigger, on, off } from "@elements/dom-utils";
import  merge  from "deepmerge"

const INITIALIZED_EVENT = 'leafletMap/initialized';

const defaultOptions = {
    mapOptions: {
        center: {lat: 47.836249, lng: 13.0615383},
        zoom: 12,
        maxZoom: 18,
    },
    pois: [],
    poiStyles: null,
    infoBoxOptions: {},
    clustering: true,
    clusteringOptions: {},
    onActivateMarker: null,
    onDeactivateMarker: null,
    tilelayer: 'https://api.tiles.mapbox.com/v4/{id}/{z}/{x}/{y}.png?access_token={accessToken}',
    leafletOptions:  {
        attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
        maxZoom: 18,
        id: 'mapbox.streets',
        accessToken: () => _config.mapAccessToken
    }
};

const defaultSelectors = {
    base: '.js-leaflet-map',
};


/* General */
export function loadLeafletMapsAPI() {
    // if (!_config.mapAccessToken) {
    //     return Promise.reject(new Error('leaflet mapAccessToken is not set. Please set _config.mapAccessToken'));
    // }

    return import('leaflet')
}

export function init(options = defaultOptions, selectors = defaultSelectors) {
    onFind(selectors.base, (baseElement) => {
        loadLeafletMapsAPI().then(() => {
/*
            defaultOptions.leafletOptions = {
                attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
                maxZoom: 18,
                id: 'mapbox.streets',
                accessToken: _config.mapAccessToken
            }
*/
            createLeafletMap(
                baseElement,
                merge.all([defaultOptions, options]),
                merge.all([defaultSelectors, selectors])
            );
        });
    });
}

export async function createLeafletMap(baseElement, options = defaultOptions, selectors = defaultSelectors) {
    let api = {
        getMapInstance,
        getMarkers,
        getActiveMarker,
        setMarker,
        addMarker,
        removeAllMarker,
        activateMarker,
        deactivateMarker,
        setActiveMarkerStyle,
        setDefaultMarkerStyle,
        centerMap,
        scrollToMarkers,
        showInfoBoxByMarker
    };

    await loadLeafletMapsAPI();

    if(options.poiStyles instanceof Function){
        options.poiStyles = options.poiStyles();
    }

    if(options.infoBoxOptions instanceof Function){
        options.infoBoxOptions = options.infoBoxOptions();
    }

    if(options.clusteringOptions instanceof Function){
        options.clusteringOptions = options.clusteringOptions();
    }

    let leafletMap = L.map(baseElement, options.mapOptions).setView([options.mapOptions.center.lat, options.mapOptions.center.lng], options.mapOptions.zoom);
    let mapLayer = L.tileLayer(options.tilelayer , options.leafletOptions);
    let MapMarker = [];
    let MapMarkeractive = null;

    mapLayer.addTo(leafletMap);


    // if (!_config.mapAccessToken) {
    //     console.error('map access key is not set. Please set _config.mapAccessToken');
    // }

    let clusteringPromise;
    if (options.clustering) {
        clusteringPromise = import("leaflet.markercluster").then(function () {
            if(options.clusteringOptions instanceof Function){
                options.clusteringOptions = options.clusteringOptions();
            }
            return L.markerClusterGroup(options.clusteringOptions);
        });
    }

    if(options.pois){
        setMarker(options.pois);
    }

    function getMapInstance(){
        return leafletMap;
    }

    function getMarkers() {
        return MapMarker;
    }

    function getActiveMarker() {
        return MapMarkeractive;
    }

    function setMarker(marker) {
        removeAllMarker();
        addMarker(marker);
        centerMap();
    }

    function addMarker(markers){
        markers.map(marker => {
            let poiStyle;

            if (marker.poiStyle) {
                if (options.poiStyles) {
                    if (options.poiStyles[marker.poiStyle]) {
                        poiStyle = options.poiStyles[marker.poiStyle];
                    } else {
                        poiStyle = options.poiStyles.default;
                        console.error(`Could not find poi style '${marker.poiStyle}' in `, options.poiStyles);
                    }
                } else {
                    console.error('Poi styles ar not set');
                }
            } else if (options.poiStyles && options.poiStyles.default) {
                poiStyle = options.poiStyles.default;
            }


            let newMarker = L.marker([marker.lat, marker.lng],{icon: poiStyle ? poiStyle.default : null});
            newMarker.poiStyle = poiStyle;
            newMarker.dataId = marker.id;
            newMarker.detailInfo = marker.detailInfo;
            newMarker.detailInfoBoxUrl = marker.detailInfoBoxUrl;

            if (options.onActivateMarker && options.onDeactivateMarker) {
                newMarker.on('click', function () {
                    if (MapMarkeractive === newMarker) {
                        deactivateMarker(newMarker);
                    } else {
                        activateMarker(newMarker);
                    }
                });
            }
            MapMarker.push(newMarker);

            if (!options.clustering) {
                newMarker.addTo(leafletMap);
            }
        });

        if (options.clustering) {
            clusteringPromise.then(function (cluster) {
                MapMarker.forEach((marker) => {
                    cluster.addLayer(marker)
                });

                leafletMap.addLayer(cluster)
            });
        }
    }


    function removeAllMarker() {
        if (options.onDeactivateMarker) {
            MapMarker.forEach(marker => options.onDeactivateMarker(marker, api));
        }

        MapMarker.forEach((marker) => {
            leafletMap.removeLayer(marker)
        });

        MapMarker = [];

        if (options.clustering) {
            clusteringPromise.then(function (cluster){
                cluster.clearLayers();
                leafletMap.removeLayer(cluster)
            });
        }
    }

    function activateMarker(marker) {
        if (MapMarkeractive) {
            deactivateMarker(MapMarkeractive);
        }

        options.onActivateMarker(marker, api);
        MapMarkeractive = marker;

        setActiveMarkerStyle(marker);

        leafletMap.setView(marker.getLatLng());
    }

    function deactivateMarker(marker) {
        options.onDeactivateMarker(MapMarkeractive, api);
        MapMarkeractive = null;

        setDefaultMarkerStyle(marker);
    }

    function setActiveMarkerStyle(marker) {
        if (marker.poiStyle) {
            marker.setIcon(marker.poiStyle.active);
        }
    }

    function setDefaultMarkerStyle(marker) {
        if (marker.poiStyle) {
            marker.setIcon(marker.poiStyle.default);
        }
    }

    function centerMap() {
        scrollToMarkers(MapMarker);
    }

    function scrollToMarkers (markers, zoomlevel = options.mapOptions.zoom) {
        let latLngs = markers.map(marker => marker._latlng);

        // Zoom/center map to fit all marker
        if (latLngs.length === 0) {
            leafletMap.panTo(options.mapOptions.center);
        } else if (latLngs.length === 1) {
            leafletMap.fitBounds(latLngs, {maxZoom: zoomlevel});
        } else {
            leafletMap.fitBounds(latLngs, {padding: [20, 20]});
        }
    }

    function showInfoBoxByMarker(marker, content) {
        if(!marker._popup) {
            marker.bindPopup(content, options.infoBoxOptions).openPopup();
        }

        return marker._popup;
    }

    baseElement.leafletMap = api;
    trigger(INITIALIZED_EVENT, baseElement);

    return api;
}


export function getApi(element) {
    if(element.leafletMap) {
        return Promise.resolve(element.leafletMap);
    } else {
        return new Promise(function(resolve, reject) {
            function initializeHandler() {
                resolve(element.leafletMap);
                off(INITIALIZED_EVENT, initializeHandler, element);
            }
            on(INITIALIZED_EVENT, initializeHandler, element);
        });
    }
}